import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import PersonalInfo from './PersonalInfo';
import Employments from './EmploymentDashboard';
import RentalHistoryDashboard from './RentalHistoryDashboard';
import CosignersCooccupants from './CosignersCooccupants';
import PetVehicleDashboard from './PetVehicleDashboard';
import ApplicationCustomQuestion from './ApplicationCustomQuestion';
import { CustomQuestionTypeEnum } from '@inosio/payments-sdk';
import ApplicationReview from './ApplicationReview';
import { Redirect, Route, Switch, useHistory, useLocation, useParams, useRouteMatch } from 'react-router';
import { useEffect, useState } from 'react';
import {
  getApplicationV2,
  getCustomQuestionsAnswersV2,
  getDocuments,
  selectApplicantsState,
  getOrganizationApplicationTCs,
} from '../sagas/applicants/applicantsSlice';
import { getPropertyGroupDetails, selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { applicationStyles } from './CustomStyles';
import BackIcon from '../../../icons/backIcon.svg';
import PropertyInformation from './PropertyInformation';
import ReleasesConsents from './ReleasesConsents';
import ApplicationRentalHistory from './ApplicationRentalHistory';
import { resetDocumentsState, selectDocumentState } from '../../../shared/sagas/document/documentSlice';
import ApplicationEmployment from './ApplicationEmployment';
import ApplicationMinor from './ApplicationMinor';
import ApplicationPet from './ApplicationPet';
import ApplicationVehicle from './ApplicationVehicle';
import {
  AdditionalQuestionsValidator,
  ApplicantPetValidator,
  ApplicantVehicleValidator,
  CoapplicantValidator,
  EmploymentIncomeValidator,
  MinorOccupantValidator,
  PersonalInfoValidator,
  PropertyInfoValidator,
  ReleasesConsentsValidator,
  RentalHistoryValidator,
} from '../validators/ApplicantValidators';

//the order here matters
export enum WizardPageStates {
  PropertyInformation = 'propertyinformation',
  PersonalDetails = 'personaldetails',
  IncomeEmployment = 'incomeemployment',
  ResidentiallHistory = 'residentialhistory',
  CosignersCooccupants = 'cosignerscooccupants',
  PetsVehicles = 'petsvehicles',
  AdditionalQuestions = 'additionalquestions',
  ReleasesConsents = 'releasesconsents',
  ReviewSubmit = 'reviewsubmit',
}

/**
 * This component servers two purposes: It is the main routing engine for the application wizard (all states),
 * and it is the central component to fetch the cross-cutting applicant information that will be shared on all the
 * tabs.
 * @param props
 * @returns
 */
export default function ApplicationWizard() {
  const { id: applicationUuidParam } = useParams<{ id: string }>();
  let { url } = useRouteMatch();
  const { pathname } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = applicationStyles();

  const { ipaUser } = useSelector(selectAuthState);
  const { Application, fetchDocumentResponse, CustomQuestionAnswers, isFetchingApplicationSuccess } = useSelector(selectApplicantsState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);
  const { isUploadingDocumentsSuccess, isDeletingDocumentSuccess } = useSelector(selectDocumentState);

  const [showPet, setShowPet] = useState(true);
  const [showVehicle, setShowVehicle] = useState(true);
  const [showAdditionalQuestions, setShowAdditionalQuestions] = useState(true);
  const [showMinor, setShowMinor] = useState(true);
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);

  //this variable tells us if the last part of the path has params or not. We use it to show/hide the tab carousal
  //a param will only appear at the end when we are editing entities (pets, vehicles, employment etc.) and in that case we should not show the carousal
  let lastPartOfPathHasParams: boolean = false;

  const employmentIncomeValidator = new EmploymentIncomeValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers, fetchDocumentResponse);
  const personalInfoValidator = new PersonalInfoValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers, fetchDocumentResponse);
  const minorOccupantValidator = new MinorOccupantValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers);
  const coapplicantValidator = new CoapplicantValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers);
  const additionalQuestionfoValidator = new AdditionalQuestionsValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers);
  const rentalHistoryValidator = new RentalHistoryValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers);
  const applicantPetValidator = new ApplicantPetValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers, fetchDocumentResponse);
  const applicantVehicleValidator = new ApplicantVehicleValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers);
  const releasesConsentsValidator = new ReleasesConsentsValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers);
  const propertyInfoValidator = new PropertyInfoValidator(propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs!, CustomQuestionAnswers);

  /**
   * Computes the current tab the user is on
   * @returns
   */
  const computeCurrentTab = () => {
    /**
     * Algorithm:
     * Check if the last part of the path is a valid tab (a WizardPageState). If it is, return the tab. In this case, show the tab carousel.
     * If the last part of the path is not a valid tab but instead a parameter, check the second-to-last part of the path. This should give you a valid tab. In this scenario, do not show the tab carousel.
     * If the second-to-last part is also not a valid tab, check if it's the edge case where you're editing a pet or a vehicle (petsvehicles/pet/:id). If it is, just return the petsvehicles tab.In this case, do not show the tab carousel as well.
     * If that doesnt give a valid tab then just return the default tab.
     */
    const parts = pathname.split('/').filter(Boolean); // Split pathname into parts and remove empty parts
    const lastPartOfPath = parts[parts.length - 1]; // Get the last part of the path
    const secondLastPartOfPath = parts[parts.length - 2]; // Get the second to last part of the path

    if (Object.values(WizardPageStates).includes(lastPartOfPath as WizardPageStates)) {
      // It's a valid tab
      return lastPartOfPath as WizardPageStates;
    } else if (parts.length > 1 && Object.values(WizardPageStates).includes(secondLastPartOfPath as WizardPageStates)) {
      // Check if the second-to-last part is a valid tab
      lastPartOfPathHasParams = true;
      return parts[parts.length - 2] as WizardPageStates;
    } else if (parts.length > 1 && (secondLastPartOfPath === 'pets' || secondLastPartOfPath === 'vehicles')) {
      // Check if the third-to-last part is a valid tab
      lastPartOfPathHasParams = true;
      return WizardPageStates.PetsVehicles;
    } else {
      // Default tab
      return WizardPageStates.PropertyInformation;
    }
  };

  const myCurrenTab = computeCurrentTab();

  // useEffect(() => {
  //   mainRef.current.scrollTo({
  //     top: 0,
  //     left: 0,
  //     behavior: 'smooth',
  //   });
  // }, [pageState]);

  //apply property group configs for tab navigation
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess) {
      setShowPet(!!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsAllowPets);
      setShowVehicle(!!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsAllowVehicles);
      setShowMinor(!!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsAllowAdditionalOccupants);
      const organizationCustomQuestions = (propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsQuestions ?? []).filter(
        (q) => q.CustomQuestionType === CustomQuestionTypeEnum.CustomV1,
      );
      setShowAdditionalQuestions(!!organizationCustomQuestions?.length && !!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsEnableCustomQuestionsSection);
    }
  }, [isFetchingPropertyGroupDetailsSuccess]);

  //step 1 fetch application metadata, all custom question answers and documents on page load
  useEffect(() => {
    dispatch({
      type: getApplicationV2.type,
      payload: applicationUuidParam,
    });

    dispatch({
      type: getCustomQuestionsAnswersV2.type,
      payload: {
        applicationUuid: applicationUuidParam,
        applicantUuid: ipaUser?.currentProfile.uuid,
      },
    });

    dispatch({
      type: getDocuments.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
      },
    });
  }, [applicationUuidParam, ipaUser]);

  //step 2 get property group details once app data loads
  useEffect(() => {
    if (isFetchingApplicationSuccess && Application) {
      dispatch({ type: getPropertyGroupDetails.type, payload: { propertyGroupUuid: Application?.OrganizationUuid, productTypeId: [2] } });
      //fetch application terms for this organization
      dispatch({ type: getOrganizationApplicationTCs.type, payload: { organizationUuid: Application.OrganizationUuid } });
    }
  }, [dispatch, Application, isFetchingApplicationSuccess]);

  //we want to refetch the document metadata any time a new doc is uploaded so our state represents reality.
  useEffect(() => {
    if (isUploadingDocumentsSuccess) {
      dispatch({
        type: getDocuments.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
        },
      });
    }
  }, [isUploadingDocumentsSuccess]);

  //* intentionally keeping the isUploadingDocumentSuccess and the isDeletingDocumentSuccess separate to prevent double fires
  useEffect(() => {
    if (isDeletingDocumentSuccess) {
      dispatch({
        type: getDocuments.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
        },
      });
    }
  }, [isDeletingDocumentSuccess]);

  //error handling
  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: WizardPageStates) => {
    dispatch(resetDocumentsState());
    dispatch({
      type: getCustomQuestionsAnswersV2.type,
      payload: {
        applicationUuid: applicationUuidParam,
        applicantUuid: ipaUser?.currentProfile.uuid,
      },
    });
    history.push(`/resui/applications/${applicationUuidParam}/${newValue.toLowerCase()}`);
  };

  const handleGoPreviousTab = () => {
    dispatch(resetDocumentsState());
    dispatch({
      type: getCustomQuestionsAnswersV2.type,
      payload: {
        applicationUuid: applicationUuidParam,
        applicantUuid: ipaUser?.currentProfile.uuid,
      },
    });
    // Check if the current tab has a parameter

    if (lastPartOfPathHasParams && myCurrenTab === WizardPageStates.PetsVehicles) {
      // If the current tab has a parameter and it's the PetsVehicles tab, navigate back to the same tab
      history.push(`/resui/applications/${applicationUuidParam}/${WizardPageStates.PetsVehicles.toLowerCase()}`);

      //refetch application data to update the ui
      dispatch({
        type: getApplicationV2.type,
        payload: applicationUuidParam,
      });
      return; // Exit the function to avoid further execution
    }

    // If the current tab has a parameter but it's not PetsVehicles, remove the parameter from the URL and navigate there
    if (lastPartOfPathHasParams) {
      const newPath = pathname.substring(0, pathname.lastIndexOf('/'));
      history.push(newPath);
      return; // Exit the function
    }

    // If the current tab does not have a parameter, navigate back using the existing logic
    const currentTabIndex = Object.values(WizardPageStates).indexOf(myCurrenTab);
    if (currentTabIndex === 0) {
      // Take them to the main application dashboard page
      history.push(`/resui/applications`);
    } else {
      let newTabIndex = currentTabIndex - 1;
      let newTabValue = Object.values(WizardPageStates)[newTabIndex];

      if (newTabValue === WizardPageStates.AdditionalQuestions && !showAdditionalQuestions) {
        // This means they have showAdditionalQuestions disabled, so skip one more.
        newTabIndex--;
        newTabValue = Object.values(WizardPageStates)[newTabIndex];
      }

      if (newTabValue === WizardPageStates.PetsVehicles && !showPet && !showVehicle) {
        // This means they have both pets and vehicles disabled, so skip one more.
        newTabIndex--;
        newTabValue = Object.values(WizardPageStates)[newTabIndex];
      }

      history.push(`/resui/applications/${applicationUuidParam}/${newTabValue.toLowerCase()}`);
    }
  };

  const handleGoNextTab = () => {
    dispatch(resetDocumentsState());
    dispatch({
      type: getCustomQuestionsAnswersV2.type,
      payload: {
        applicationUuid: applicationUuidParam,
        applicantUuid: ipaUser?.currentProfile.uuid,
      },
    });

    // Check if the current tab has a parameter

    if (lastPartOfPathHasParams && myCurrenTab === WizardPageStates.PetsVehicles) {
      // If the current tab has a parameter and it's the PetsVehicles tab, navigate back to the same tab
      history.push(`/resui/applications/${applicationUuidParam}/${WizardPageStates.PetsVehicles.toLowerCase()}`);
      //refetch application data to update the ui
      dispatch({
        type: getApplicationV2.type,
        payload: applicationUuidParam,
      });
      return; // Exit the function to avoid further execution
    }

    // If the current tab has a parameter but it's not PetsVehicles, remove the parameter from the URL and navigate there
    if (lastPartOfPathHasParams) {
      const newPath = pathname.substring(0, pathname.lastIndexOf('/'));
      history.push(newPath);
      return; // Exit the function
    }

    if (myCurrenTab === WizardPageStates.ReviewSubmit) {
      // Error case, they shouldn't be able to click next on the final tab
      // Handle this case if needed
    } else {
      // Calculate the index of the current tab
      const currentTabIndex = Object.values(WizardPageStates).indexOf(myCurrenTab);
      let newTabIndex = currentTabIndex + 1;
      let newTabValue = Object.values(WizardPageStates)[newTabIndex];

      // Check if the next tab requires skipping due to disabled options
      if (newTabValue === WizardPageStates.PetsVehicles && !showPet && !showVehicle) {
        // This means they have both pets and vehicles disabled, so skip one more.
        newTabIndex++;
        newTabValue = Object.values(WizardPageStates)[newTabIndex];
      }

      if (newTabValue === WizardPageStates.AdditionalQuestions && !showAdditionalQuestions) {
        // This means they have showAdditionalQuestions disabled, so skip one more.
        newTabIndex++;
        newTabValue = Object.values(WizardPageStates)[newTabIndex];
      }

      // Navigate to the next tab
      history.push(`/resui/applications/${applicationUuidParam}/${newTabValue.toLowerCase()}`);
    }
  };

  const handleSubmit = () => {};

  const tabSx = {
    textTransform: 'capitalize',
    '&.Mui-selected': {
      color: '#000 !important',
      border: 'none !important',
    },
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ background: '#FFFFFF', borderRadius: '24px', position: 'relative' }}>
      {/*We dont show a spinner here because we already show spinners on the child components for the property group call */}
      {showLocalSpinner ? (
        <Box></Box>
      ) : (
        <Box pt={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', height: '70px', alignItems: 'center', px: 2, borderBottom: '1px solid #EEEEEE', pb: 2 }}>
            <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={handleGoPreviousTab}>
              <img
                style={{
                  width: '20px',
                  height: '20px',
                }}
                src={BackIcon}
                alt="backicon"
              />
              <Typography sx={{ ml: 1, color: '#39A2DB' }} className={classes.labelText}>
                {`Back`}
              </Typography>
            </Box>
            <Box>
              {myCurrenTab !== WizardPageStates.ReviewSubmit && (
                <Button type="button" className={classes.wizardNextBtn} onClick={handleGoNextTab}>
                  <Typography className={classes.wizardNextBtnText} sx={{ margin: '0 10px' }}>
                    {`Continue`}
                  </Typography>
                </Button>
              )}
            </Box>
          </Box>
          {!lastPartOfPathHasParams && (
            <Box sx={{ px: 2, pt: 1 }}>
              <Tabs
                value={myCurrenTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="scrollable auto tabs"
                sx={{
                  '& .MuiTabs-flexContainer': {
                    width: '100%',
                    maxWidth: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none !important',
                  },
                }}
              >
                <Tab sx={tabSx} className={classes.wizardText} label="Property Information" value={WizardPageStates.PropertyInformation} />
                <Tab sx={tabSx} className={classes.wizardText} label="Personal Details" value={WizardPageStates.PersonalDetails} />
                <Tab sx={tabSx} className={classes.wizardText} label="Income & Employment" value={WizardPageStates.IncomeEmployment} />
                <Tab sx={tabSx} className={classes.wizardText} label="Residential History" value={WizardPageStates.ResidentiallHistory} />
                <Tab
                  sx={{
                    ...tabSx,
                    maxWidth: showMinor ? '120px' : '155px !important',
                  }}
                  className={classes.wizardText}
                  label={showMinor ? 'Additional Occupants' : 'Co-applicants & Co-signers'}
                  value={WizardPageStates.CosignersCooccupants}
                />
                {(showPet || showVehicle) && (
                  <Tab
                    sx={tabSx}
                    className={classes.wizardText}
                    label={showPet && showVehicle ? 'Animals & Vehicles' : showPet ? 'Animals' : showVehicle ? 'Vehicles' : 'So bad'}
                    value={WizardPageStates.PetsVehicles}
                  />
                )}
                {showAdditionalQuestions && <Tab sx={tabSx} className={classes.wizardText} label="Additional Questions" value={WizardPageStates.AdditionalQuestions} />}
                <Tab sx={tabSx} className={classes.wizardText} label="Releases & Consents" value={WizardPageStates.ReleasesConsents} />
                <Tab sx={tabSx} className={classes.wizardText} label="Review Submit" value={WizardPageStates.ReviewSubmit} />
              </Tabs>
            </Box>
          )}
          {localError && <Typography className={classes.globalText}>{localError}</Typography>}
        </Box>
      )}

      <Switch>
        <Route path={`${url}/${WizardPageStates.PropertyInformation.toLowerCase()}`}>
          <PropertyInformation appUuid={applicationUuidParam} />
        </Route>
        <Route path={`${url}/${WizardPageStates.PersonalDetails.toLowerCase()}`}>
          <PersonalInfo />
        </Route>
        <Route path={`${url}/${WizardPageStates.IncomeEmployment.toLowerCase()}/:id`}>
          <ApplicationEmployment />
        </Route>
        <Route path={`${url}/${WizardPageStates.IncomeEmployment.toLowerCase()}`}>
          <Employments Validator={employmentIncomeValidator} />
        </Route>
        {/**if there was a param provided, means it is create/edit rental history so render that component */}
        <Route path={`${url}/${WizardPageStates.ResidentiallHistory.toLowerCase()}/:id`}>
          <ApplicationRentalHistory />
        </Route>
        <Route path={`${url}/${WizardPageStates.ResidentiallHistory.toLowerCase()}`}>
          <RentalHistoryDashboard Validator={rentalHistoryValidator} />
        </Route>
        <Route path={`${url}/${WizardPageStates.CosignersCooccupants.toLowerCase()}/:id`}>
          <ApplicationMinor />
        </Route>
        <Route path={`${url}/${WizardPageStates.CosignersCooccupants.toLowerCase()}`}>
          <CosignersCooccupants CoapplicantsValidator={coapplicantValidator} MinorOccupantsValidator={minorOccupantValidator} />
        </Route>
        <Route path={`${url}/pets/:id`}>
          <ApplicationPet />
        </Route>
        <Route path={`${url}/vehicles/:id`}>
          <ApplicationVehicle />
        </Route>
        <Route path={`${url}/${WizardPageStates.PetsVehicles.toLowerCase()}`}>
          <PetVehicleDashboard petsValidator={applicantPetValidator} vehiclesValidator={applicantVehicleValidator} />
        </Route>
        <Route path={`${url}/${WizardPageStates.AdditionalQuestions.toLowerCase()}`}>
          <ApplicationCustomQuestion />
        </Route>
        <Route path={`${url}/${WizardPageStates.ReleasesConsents.toLowerCase()}`}>
          <ReleasesConsents appUuid={applicationUuidParam} releasesConsentsValidator={releasesConsentsValidator} />
        </Route>
        <Route path={`${url}/${WizardPageStates.ReviewSubmit.toLowerCase()}`}>
          <ApplicationReview
            appUuid={applicationUuidParam}
            PersonalInfoValidator={personalInfoValidator}
            EmployeeValidator={employmentIncomeValidator}
            MinorOccupantValidator={minorOccupantValidator}
            AdditionalQuestionsValidator={additionalQuestionfoValidator}
            CoapplicantValidator={coapplicantValidator}
            PropertyInfoValidator={propertyInfoValidator}
            ReleasesConsentsValidator={releasesConsentsValidator}
            RentalHistoryValidator={rentalHistoryValidator}
            PetsValidator={applicantPetValidator}
            VehiclesValidator={applicantVehicleValidator}
          />
        </Route>
        <Route exact path={url}>
          <Redirect to={`${url}/${WizardPageStates.PropertyInformation.toLowerCase()}`} />
        </Route>
      </Switch>
    </Box>
  );
}
